import "./DragGhost.css";

/**
 * Значок под курсором при перетаскивании карточки видео или папки.
 *
 * Стандартный «снимок» карточки браузер рисует сам и анимировать его нельзя,
 * поэтому он подменяется пустой картинкой, а значок рисуется своим элементом,
 * который следует за курсором по событиям dragover.
 *
 * startDragGhost(e, {...}) — вызвать в onDragStart карточки.
 * setDragGhostTarget / clearDragGhostTarget — вызывает DropTarget при наведении.
 * Убирается сам по dragend / drop.
 */

const EMPTY_IMAGE = new Image();
EMPTY_IMAGE.src =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const SVG_ATTRS =
  'width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"';

const ICONS = {
  video: `<svg ${SVG_ATTRS}><rect x="2.5" y="5" width="19" height="14" rx="3"/><path d="M10 9.2v5.6l4.6-2.8z" fill="currentColor"/></svg>`,
  folder: `<svg ${SVG_ATTRS}><path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h3.6l2 2.2h7.4A2.5 2.5 0 0 1 21 9.7v7.8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z"/></svg>`,
};

const HINT_DEFAULT = "Переместить в…";
const HINT_SAME = {
  video: "Видео уже в этой папке",
  folder: "Папка уже здесь",
};
const HINT_SELF = "Нельзя переместить папку в саму себя";

// Ключ места: id папки строкой или "root" — корень пространства
const placeKey = (projectId, isRoot) =>
  isRoot ? "root" : projectId != null ? String(projectId) : null;

// Значок встаёт правее и ниже курсора, как системный
const CURSOR_GAP = 14;
const ICON_SIZE = 44;

let ghost = null;

const reduceMotion = () =>
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const place = (x, y) => {
  ghost.root.style.transform = `translate(${x + CURSOR_GAP}px, ${
    y + CURSOR_GAP
  }px)`;
};

const onDragOver = (e) => {
  // Firefox иногда присылает нули — пропускаем, иначе значок прыгает в угол
  if (!ghost || (e.clientX === 0 && e.clientY === 0)) return;
  place(e.clientX, e.clientY);
};

const onFinish = () => endDragGhost();

// drop ловится на document раньше, чем DropTarget его обработает, а цели
// нужно знать, откуда взят элемент (getDragGhostSource) — снимаем значок после
const onDropFinish = () => setTimeout(endDragGhost, 0);

// Кнопка мыши отпущена, а значок ещё висит — значит, dragend потерялся
const onMouseMove = (e) => {
  if (e.buttons === 0) endDragGhost();
};

export const startDragGhost = (
  e,
  { kind = "video", poster, sourceEl, fromProjectId, itemId }
) => {
  endDragGhost();
  const source = sourceEl || e.currentTarget;
  const rect = source.getBoundingClientRect();

  e.dataTransfer.setDragImage(EMPTY_IMAGE, 0, 0);

  const root = document.createElement("div");
  root.className = "drag-ghost";
  root.setAttribute("aria-hidden", "true");

  const box = document.createElement("div");
  box.className = "drag-ghost__box";

  const posterEl = document.createElement("div");
  posterEl.className = "drag-ghost__poster";
  if (poster) posterEl.style.backgroundImage = `url(${JSON.stringify(poster)})`;

  const icon = document.createElement("div");
  icon.className = "drag-ghost__icon";
  icon.innerHTML = ICONS[kind] || ICONS.video;

  const hint = document.createElement("div");
  hint.className = "drag-ghost__hint";
  hint.textContent = HINT_DEFAULT;

  box.append(posterEl, icon);
  root.append(box, hint);
  document.body.appendChild(root);

  ghost = {
    root,
    box,
    hint,
    kind,
    // fromProjectId === null у папки — она лежит в корне пространства
    fromKey: placeKey(fromProjectId, fromProjectId === null),
    itemKey: itemId != null ? String(itemId) : null,
    source,
    sourceOpacity: source.style.opacity,
    targetOwner: null,
  };
  place(e.clientX, e.clientY);

  // Карточка на месте тускнеет: видно, что её «взяли»
  source.style.opacity = "0.35";

  if (!reduceMotion()) {
    // Старт — размер и положение карточки, финиш — значок у курсора
    const dx = rect.left - (e.clientX + CURSOR_GAP);
    const dy = rect.top - (e.clientY + CURSOR_GAP);
    const timing = { duration: 220, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" };
    box.animate(
      [
        {
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          transform: `translate(${dx}px, ${dy}px)`,
          borderRadius: "16px",
        },
        {
          width: `${ICON_SIZE}px`,
          height: `${ICON_SIZE}px`,
          transform: "translate(0, 0)",
          borderRadius: "12px",
        },
      ],
      timing
    );
    posterEl.animate([{ opacity: 1 }, { opacity: 0 }], timing);
    icon.animate(
      [
        { opacity: 0, transform: "scale(0.6)" },
        { opacity: 1, transform: "scale(1)" },
      ],
      { ...timing, delay: 80, fill: "backwards" }
    );
    hint.animate(
      [
        { opacity: 0, transform: "translate(-6px, -50%)" },
        { opacity: 1, transform: "translate(0, -50%)" },
      ],
      { duration: 160, delay: 160, easing: "ease-out", fill: "backwards" }
    );
  }

  document.addEventListener("dragover", onDragOver, true);
  document.addEventListener("drop", onDropFinish, true);
  document.addEventListener("dragend", onFinish, true);
  // Страховка: если карточку убрали из DOM во время переноса, dragend
  // может не прийти, и значок остался бы висеть.
  document.addEventListener("mousemove", onMouseMove, true);
};

/**
 * Что сейчас тащат: { kind, fromKey, itemKey } или null.
 * fromKey — место, откуда взят элемент; itemKey — id самой папки (для kind "folder").
 */
export const getDragGhostSource = () =>
  ghost
    ? { kind: ghost.kind, fromKey: ghost.fromKey, itemKey: ghost.itemKey }
    : null;

/** Цель — то же место, откуда элемент взят. */
export const isDragGhostSameFolder = (projectId, isRoot = false) => {
  const key = placeKey(projectId, isRoot);
  return !!ghost && key != null && ghost.fromKey === key;
};

/** Цель — сама перетаскиваемая папка. */
export const isDragGhostSelf = (projectId) =>
  !!ghost &&
  ghost.kind === "folder" &&
  projectId != null &&
  ghost.itemKey === String(projectId);

/**
 * Курсор над папкой (или корнем пространства, isRoot).
 * owner — любой уникальный объект цели (например, ref), чтобы уход
 * с одной папки не сбросил подсказку, уже выставленную соседней.
 */
export const setDragGhostTarget = (owner, { label, projectId, isRoot = false }) => {
  if (!ghost) return;
  ghost.targetOwner = owner;
  const isSelf = !isRoot && isDragGhostSelf(projectId);
  const isSame = isDragGhostSameFolder(projectId, isRoot);
  const blocked = isSelf || isSame;

  ghost.root.classList.toggle("drag-ghost--over", !blocked);
  ghost.root.classList.toggle("drag-ghost--same", blocked);
  ghost.hint.textContent = isSelf
    ? HINT_SELF
    : isSame
    ? HINT_SAME[ghost.kind] || HINT_SAME.video
    : label
    ? `Переместить в «${label}»`
    : HINT_DEFAULT;
};

export const clearDragGhostTarget = (owner) => {
  if (!ghost || ghost.targetOwner !== owner) return;
  ghost.targetOwner = null;
  ghost.root.classList.remove("drag-ghost--over", "drag-ghost--same");
  ghost.hint.textContent = HINT_DEFAULT;
};

export const endDragGhost = () => {
  if (!ghost) return;
  ghost.source.style.opacity = ghost.sourceOpacity;
  ghost.root.remove();
  ghost = null;
  document.removeEventListener("dragover", onDragOver, true);
  document.removeEventListener("drop", onDropFinish, true);
  document.removeEventListener("dragend", onFinish, true);
  document.removeEventListener("mousemove", onMouseMove, true);
};
